import { Bar, getElementAtEvent } from 'react-chartjs-2';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import 'chartjs-adapter-luxon';
import Box from '@mui/material/Box';
import {
  GRAPH_DEFAULT_OPTIONS,
  STATUS_BORDER_COLOR_MAP,
  STATUS_COLOR_MAP,
  chartBorderPlugin,
} from '../graphConfig';
import { highlightPlugin, middleLine } from '../utils/graphPlugins';
import { useDispatchEventToChart } from '../common/Graphs/hooks/useDispatchEventToChart';
import { useGraphTooltipConfig } from './hooks/useGraphTooltipConfig';
import { useGraphZoomConfig } from '../common/Graphs/hooks/useGraphZoomConfig';
import { palette } from 'theme/palette';
import { EmptyLineOrBarChart } from '../common/Graphs/EmptyLineOrBarChart';
import { pxToRem } from 'src/common/utils/fonts';
import { Chart, ChartOptions } from 'chart.js';

const HEIGHT = 100;

export type ChartEntryData = {
  x: [Date, Date];
  y: string;
  dataIndex: number;
};

export type RSimSessionGraphRangeProps = {
  shouldDisplayPlaceholderChart?: boolean;
  primaryEntries: ChartEntryData[];
  backupEntries: ChartEntryData[];
  from: number;
  to: number;
  min: number;
  max: number;
  highlightFrom?: Date | null;
  highlightTo?: Date | null;
  onZoomComplete: (range: [Date, Date]) => void;
  tooltip: (positionX: number, index: number | null, setName: string | null) => void;
  onClick: (index: number, setName: 'Primary' | 'Backup' | null) => void;
  onPan?: (newMin: Date, newMax: Date) => void;
};

const DEFAULT_BAR_SETTINGS = {
  borderWidth: 1,
  borderRadius: 4,
  borderSkipped: false,
  barThickness: 20,
};

const prepareBarData = (
  entries: ChartEntryData[],
  filterValue: string,
  type: 'Primary' | 'Backup',
) => {
  return entries
    .filter((entry) => entry.y === filterValue)
    .map((entry) => ({
      x: entry.x,
      y: type,
      dataIndex: entry.dataIndex,
    }));
};

const prepareSimDatasets = (entries: ChartEntryData[], type: 'Primary' | 'Backup') => {
  const normalDatasets = {
    ...DEFAULT_BAR_SETTINGS,
    label: 'NORMAL',
    data: prepareBarData(entries, 'NORMAL', type),
    backgroundColor: STATUS_COLOR_MAP.NORMAL,
    borderColor: STATUS_BORDER_COLOR_MAP.NORMAL,
  };
  const notFinishedDatasets = {
    ...DEFAULT_BAR_SETTINGS,
    label: 'NOT FINISHED',
    data: prepareBarData(entries, 'NOT_FINALIZED', type),
    backgroundColor: STATUS_COLOR_MAP.NOT_FINALIZED,
    borderColor: STATUS_BORDER_COLOR_MAP.NOT_FINALIZED,
  };

  const zeroByteDatasets = {
    ...DEFAULT_BAR_SETTINGS,
    label: 'ZERO BYTE SESSION',
    data: prepareBarData(entries, 'ZERO_BYTE_SESSION', type),
    backgroundColor: STATUS_COLOR_MAP.ZERO_BYTE_SESSION,
    borderColor: STATUS_BORDER_COLOR_MAP.ZERO_BYTE_SESSION,
  };

  return [normalDatasets, notFinishedDatasets, zeroByteDatasets];
};

export const RSimSessionGraphRange: React.FC<RSimSessionGraphRangeProps> = React.memo(
  ({
    shouldDisplayPlaceholderChart,
    primaryEntries,
    backupEntries,
    from,
    to,
    min,
    max,
    highlightFrom,
    highlightTo,
    onZoomComplete,
    tooltip,
    onClick,
    onPan,
  }) => {
    const { t } = useTranslation();

    const barData = useMemo(() => {
      return {
        labels: ['Primary', 'Backup'],
        datasets: [
          ...prepareSimDatasets(primaryEntries, 'Primary'),
          ...prepareSimDatasets(backupEntries, 'Backup'),
        ],
      };
    }, [primaryEntries, backupEntries]);

    const tooltipConfig = useGraphTooltipConfig(from, to, tooltip);
    const zoomConfig = useGraphZoomConfig(5 * 60_000, min, max, onZoomComplete, onPan);

    // @ts-expect-error -- resolve options
    const barOptions: ChartOptions<'bar'> = useMemo(
      () => ({
        ...GRAPH_DEFAULT_OPTIONS,
        indexAxis: 'y',
        scales: {
          x: {
            ...GRAPH_DEFAULT_OPTIONS!.scales!.x,
            type: 'time',
            min: new Date(from),
            max: new Date(to),
          },
          y: {
            ...GRAPH_DEFAULT_OPTIONS!.scales!.y,
            stacked: true,
            beginAtZero: false,
            display: false,
          },
        },
        plugins: {
          ...GRAPH_DEFAULT_OPTIONS!.plugins,
          zoom: zoomConfig,
          highlightPlugin: {
            from: highlightFrom,
            to: highlightTo,
          },
          middleLine: {
            borderColor: palette.grey['400'],
            borderWidth: 1,
          },
          tooltip: tooltipConfig,
        },
      }),
      [from, to, barData, highlightFrom, highlightTo, tooltipConfig, zoomConfig],
    );

    const chartRef = useRef<Chart>(undefined);
    const onChartClick = (event: any) => {
      if (chartRef.current) {
        const elementsAtEvent = getElementAtEvent(chartRef.current, event);
        if (elementsAtEvent.length === 0) {
          return;
        }

        const [chartElement] = elementsAtEvent;

        // @ts-expect-error -- get raw in a typed way
        const pointRaw = chartElement.element['$context'].raw;

        const index = pointRaw.dataIndex;

        const setName = pointRaw.y;

        onClick(index, setName);
      }
    };

    useDispatchEventToChart(chartRef);

    if (shouldDisplayPlaceholderChart) {
      return (
        <Box sx={{ height: HEIGHT }}>
          <EmptyLineOrBarChart
            as={Bar}
            emptyChartText={t('rSimInventory.imsiMissing')}
            // @ts-expect-error -- resolve options
            xLabelOptions={barOptions.scales.x}
          />
        </Box>
      );
    }

    return (
      <Box sx={{ height: pxToRem(HEIGHT) }}>
        <Bar
          options={barOptions}
          data={barData}
          plugins={[chartBorderPlugin, middleLine, highlightPlugin]}
          // @ts-expect-error -- make ref type match chart and data types
          ref={chartRef}
          onClick={onChartClick}
        />
      </Box>
    );
  },
);
